// Box data types (03 §3.4 Box, 03 §3.6 state machine).

export const BOX_STATUSES = [
  'at_branch',
  'on_trip',
  'in_container',
  'exported',
  'at_port',
  'with_partner',
  'out_for_delivery',
  'delivered',
  'returned',
  'cancelled',
  'lost',
] as const;
export type BoxStatus = (typeof BOX_STATUSES)[number];

/** States with no way out through a normal command. A physical scan can still move the box. */
export const FINAL_STATUSES: readonly BoxStatus[] = ['delivered', 'returned', 'cancelled', 'lost'];

export function isFinalStatus(status: BoxStatus): boolean {
  return FINAL_STATUSES.includes(status);
}

export type BoxFlag = 'damaged' | 'unaccounted' | 'returning' | 'abandoned';
export type BoxFlags = Readonly<Record<BoxFlag, boolean>>;

export const NO_FLAGS: BoxFlags = {
  damaged: false,
  unaccounted: false,
  returning: false,
  abandoned: false,
};

export type LocationKind =
  'branch' | 'trip' | 'container' | 'run' | 'partner' | 'consignee' | 'unknown';
export interface BoxLocation {
  readonly kind: LocationKind;
  /** Branch, trip, container, run or partner id. Shipment id for `consignee`. Empty for `unknown`. */
  readonly id: string;
}

export const PARTNER_STATUSES = [
  'received_by_partner',
  'out_for_delivery',
  'failed_attempt',
  'delivered',
  'damaged',
  'lost',
  'returned_to_hub',
] as const;
export type PartnerStatus = (typeof PARTNER_STATUSES)[number];

export interface BoxPartner {
  readonly partnerId: string;
  readonly handoverId: string;
  readonly partnerStatus: PartnerStatus;
  readonly partnerRefs: readonly string[];
  readonly lastUpdateAt: string;
}

export type HoldType =
  'customs' | 'payment' | 'investigation' | 'customer_request' | 'address_issue';
export interface Hold {
  readonly type: HoldType;
  readonly reason: string;
  readonly placedBy: string;
  readonly placedAt: string;
  readonly releasedBy?: string;
  readonly releasedAt?: string;
}

/** Hold types that also stop trips and container loading. The rest only stop delivery and handover (03 §3.4 Holds). */
export const MOVEMENT_BLOCKING_HOLDS: readonly HoldType[] = ['investigation', 'customer_request'];

export function activeHolds(box: Pick<Box, 'holds'>): Hold[] {
  return box.holds.filter((hold) => hold.releasedAt === undefined);
}

export function hasActiveHold(box: Pick<Box, 'holds'>): boolean {
  return activeHolds(box).length > 0;
}

export function hasBlockingHold(box: Pick<Box, 'holds'>): boolean {
  return activeHolds(box).some((hold) => MOVEMENT_BLOCKING_HOLDS.includes(hold.type));
}

export interface LastEvent {
  readonly type: BoxEventType;
  readonly at: string;
  readonly branchId: string;
  readonly by?: string;
  readonly deviceId?: string;
  /** Identity of the event for duplicate detection: type, carrier (and partner status). */
  readonly dedupeKey: string;
}

export interface Box {
  readonly barcode: string;
  readonly shipmentId: string;
  readonly status: BoxStatus;
  readonly location: BoxLocation;
  /** The last branch that received the box. Changes only on receive/collect events. */
  readonly currentBranchId: string;
  readonly tripId?: string;
  readonly containerId?: string;
  readonly runId?: string;
  readonly partner?: BoxPartner;
  readonly holds: readonly Hold[];
  readonly flags: BoxFlags;
  readonly lastEvent?: LastEvent;
  readonly version: number;
}

// ---------------------------------------------------------------- events

interface EventBase {
  readonly barcode: string;
  /** ISO 8601 instant with `Z` or an offset: device time, clock-corrected. */
  readonly occurredAt: string;
  /** The branch where the scan happened. */
  readonly branchId: string;
  readonly by?: string;
  readonly deviceId?: string;
  readonly commandId?: string;
}

export interface CollectEvent extends EventBase {
  readonly type: 'collect';
  readonly shipmentId: string;
}
export interface LoadTripEvent extends EventBase {
  readonly type: 'loadTrip';
  readonly tripId: string;
}
export interface UnloadFromTripEvent extends EventBase {
  readonly type: 'unloadFromTrip';
  readonly tripId: string;
}
export interface ReceiveTripEvent extends EventBase {
  readonly type: 'receiveTrip';
  readonly tripId: string;
  /** The trip's destination branch. A scan anywhere else is a wrong-destination exception. */
  readonly toBranchId: string;
}
export interface LoadContainerEvent extends EventBase {
  readonly type: 'loadContainer';
  readonly containerId: string;
}
export interface RemoveFromContainerEvent extends EventBase {
  readonly type: 'removeFromContainer';
  readonly containerId: string;
}
export interface ExportContainerEvent extends EventBase {
  readonly type: 'exportContainer';
  readonly containerId: string;
}
export interface ContainerArrivedEvent extends EventBase {
  readonly type: 'containerArrived';
  readonly containerId: string;
}
export interface ReceiveContainerEvent extends EventBase {
  readonly type: 'receiveContainer';
  readonly containerId: string;
  /** The container's import hub. A scan anywhere else is a wrong-destination exception. */
  readonly importHubId: string;
}
export interface HandoverToPartnerEvent extends EventBase {
  readonly type: 'handoverToPartner';
  readonly partnerId: string;
  readonly handoverId: string;
  readonly partnerRefs?: readonly string[];
}
export interface PartnerUpdateEvent extends EventBase {
  readonly type: 'partnerUpdate';
  readonly partnerId: string;
  readonly handoverId: string;
  readonly partnerStatus: PartnerStatus;
  readonly partnerRefs?: readonly string[];
}
export interface ReturnFromPartnerEvent extends EventBase {
  readonly type: 'returnFromPartner';
  readonly handoverId: string;
}
export interface StartRunEvent extends EventBase {
  readonly type: 'startRun';
  readonly runId: string;
}
export interface DeliverStopEvent extends EventBase {
  readonly type: 'deliverStop';
  readonly runId: string;
  readonly stopId: string;
}
export interface CloseRunEvent extends EventBase {
  readonly type: 'closeRun';
  readonly runId: string;
}
export interface CompleteReturnEvent extends EventBase {
  readonly type: 'completeReturn';
}
export interface CancelShipmentEvent extends EventBase {
  readonly type: 'cancelShipment';
}
/** `resolveException` with a write-off outcome. */
export interface WriteOffEvent extends EventBase {
  readonly type: 'writeOff';
  readonly exceptionId: string;
}
/** `resolveException` with a "found" outcome: a missing box turned up at `branchId`. */
export interface FoundEvent extends EventBase {
  readonly type: 'found';
  readonly exceptionId: string;
}

export type BoxEvent =
  | CollectEvent
  | LoadTripEvent
  | UnloadFromTripEvent
  | ReceiveTripEvent
  | LoadContainerEvent
  | RemoveFromContainerEvent
  | ExportContainerEvent
  | ContainerArrivedEvent
  | ReceiveContainerEvent
  | HandoverToPartnerEvent
  | PartnerUpdateEvent
  | ReturnFromPartnerEvent
  | StartRunEvent
  | DeliverStopEvent
  | CloseRunEvent
  | CompleteReturnEvent
  | CancelShipmentEvent
  | WriteOffEvent
  | FoundEvent;

export type BoxEventType = BoxEvent['type'];

export const BOX_EVENT_TYPES = [
  'collect',
  'loadTrip',
  'unloadFromTrip',
  'receiveTrip',
  'loadContainer',
  'removeFromContainer',
  'exportContainer',
  'containerArrived',
  'receiveContainer',
  'handoverToPartner',
  'partnerUpdate',
  'returnFromPartner',
  'startRun',
  'deliverStop',
  'closeRun',
  'completeReturn',
  'cancelShipment',
  'writeOff',
  'found',
] as const satisfies readonly BoxEventType[];

/** Events that record the box arriving at a branch: the only ones allowed to change `currentBranchId`. */
export const BRANCH_RECEIVING_EVENTS: readonly BoxEventType[] = [
  'collect',
  'receiveTrip',
  'receiveContainer',
  'returnFromPartner',
  'closeRun',
  'found',
];

// ----------------------------------------------------------- outcomes

/** Facts the box cannot know, supplied by the command handler from the shipment. */
export interface BoxContext {
  /** True only when the shipment is paid in full (D-16). Anything else refuses loading and handover. */
  readonly shipmentPaidInFull?: boolean;
  /** True once any box of the shipment is exported: cancellation is then refused. */
  readonly anyBoxExported?: boolean;
}

export type BoxExceptionType = 'illegal_scan' | 'wrong_destination' | 'damaged' | 'missing';
export interface BoxException {
  readonly type: BoxExceptionType;
  readonly reason: string;
}

export type BoxRefusalReason =
  | 'unknown_box'
  | 'barcode_already_attached'
  | 'unpaid'
  | 'active_hold'
  | 'blocking_hold'
  | 'not_returning'
  | 'shipment_exported'
  | 'illegal_state';

export type BoxOutcome =
  | { readonly result: 'ok'; readonly box: Box }
  | { readonly result: 'duplicate'; readonly box: Box }
  | { readonly result: 'exception'; readonly box: Box; readonly exception: BoxException }
  | { readonly result: 'refused'; readonly reason: BoxRefusalReason };
